import React, { useState, useEffect } from 'react';
import { useDarkMode } from '../../context/DarkModeContext';
import { EditModeProps } from './types';
import { SchemaField, CardData, CareerCard } from '../../types/careerCard';
import { useCareerCard } from '../../context/CareerCardContext';
import { useModifiedFields } from '../../hooks/useModifiedFields';
import { createLogger } from '../../utils/logger';
import { SuggestionPanel } from './SuggestionPanel';
import { Suggestion } from '../../types/suggestions';
import { IoTrashOutline, IoAddOutline, IoLinkOutline, IoChevronUpOutline, IoChevronDownOutline } from 'react-icons/io5';
import { LinkedContent, LinkedContentPreview } from '../LinkedContentPreview';
import { useCachedData } from '../../hooks/useCachedData';
import { CACHE_KEYS, CACHE_DURATIONS } from '../../constants/cacheKeys';
import { supabase } from '../../utils/supabase';
import { env } from '../../utils/env';
import { useToast } from '../../context/ToastContext';
import GoalSelector from '../CareerFocus/GoalSelector';
import RubricCriteriaSelector from '../CareerFocus/RubricCriteriaSelector';
import { useActiveCareerFocus } from '../../hooks/useCareerFocus';
import { useRubric } from '../../hooks/useRubric';
import { motion } from 'framer-motion';

const logger = createLogger({ module: 'EditMode', level: 'info' });

interface ExtendedSchemaField extends SchemaField {
  options?: string[];
}

const titleCase = (str: string) => {
  return str
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

interface ArrayInputProps {
  label: string;
  values: string[];
  onChange: (newValues: string[]) => void;
}

const ArrayInput: React.FC<ArrayInputProps> = ({ label, values, onChange }) => {
  const [newValue, setNewValue] = React.useState('');

  const handleAdd = () => {
    if (!newValue.trim()) return;
    const value = newValue.trim();
    if (!values.includes(value)) {
      onChange([...values, value]);
    }
    setNewValue('');
  };

  const handleRemove = (valueToRemove: string) => {
    onChange(values.filter(v => v !== valueToRemove));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2 mb-2">
        {values.map((value, index) => (
          <span
            key={index}
            className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center gap-1"
          >
            {value}
            <button
              onClick={() => handleRemove(value)}
              className="ml-1 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
            >
              ×
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          type="text"
          value={newValue}
          onChange={(e) => setNewValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={`Add ${label.toLowerCase()}`}
          className="grow p-2 rounded-lg border dark:bg-gray-700 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <button
          onClick={handleAdd}
          disabled={!newValue.trim()}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
        >
          <IoAddOutline size={20} />
          Add
        </button>
      </div>
    </div>
  );
};

export function EditMode({ 
  card, 
  onChange, 
  selectedCardType, 
  schema,
  linkedContent,
  onLinkWorkExperience,
  onUnlinkWorkExperience,
  renderFollowUpActions
}: EditModeProps) {
  const { darkMode } = useDarkMode();
  const { focus } = useActiveCareerFocus();
  const { activeRubric } = useRubric(focus?.id);
  const [modifiedFields, setModifiedFields] = useState<Set<string>>(new Set());
  const { schemas } = useCareerCard();
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [acceptedFields, setAcceptedFields] = useState<Set<string>>(new Set());
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [previousValues, setPreviousValues] = useState<Record<string, unknown>>({});
  const [newSuggestions, setNewSuggestions] = useState<Suggestion[]>([]);
  const [newTag, setNewTag] = useState('');
  const [suggestedTags, setSuggestedTags] = useState<string[]>([]);
  const [isGeneratingTags, setIsGeneratingTags] = useState(false);
  const [isFocusLinkageExpanded, setIsFocusLinkageExpanded] = useState(false);
  const { showToast } = useToast();

  const {
    data: workExperiences = [],
  } = useCachedData<LinkedContent[]>(
    async () => {
      const { data, error } = await supabase
        .from('work_experience')
        .select('*')
        .order('start_date', { ascending: false });

      if (error) throw error;

      return data.map(exp => ({
        id: exp.id,
        type: 'work_experience' as const,
        preview: {
          company: exp.company,
          position: exp.position,
          date_range: {
            start: exp.start_date,
            end: exp.end_date || undefined,
          },
        },
      }));
    },
    {
      cacheKey: CACHE_KEYS.WORK_EXPERIENCES,
      maxAge: CACHE_DURATIONS.SHORT,
      initialData: [],
    }
  );

  // Reset edited fields when card changes
  useEffect(() => {
    setModifiedFields(new Set());
    setSuggestions([]);
    setAcceptedFields(new Set());
    setPreviousValues({});
  }, [card.cardid]);

  const handleFieldChange = (field: string, value: unknown) => {
    logger.debug('EditMode handleFieldChange:', { field, value });
    onChange({
      ...card,
      card_data: {
        ...card.card_data,
        [field]: value
      }
    });
  };

  const handleGetSuggestions = async () => {
    try {
      setIsLoadingSuggestions(true);
      const response = await fetch(`${env.VITE_SUPABASE_URL}/functions/v1/suggest-card-updates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          cardData: card.card_data,
          cardSchema: schema,
          includePrompts: true,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`Failed to get suggestions: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      if (!data.suggestions || !Array.isArray(data.suggestions)) {
        throw new Error('Invalid response format from suggestions API');
      }

      const validSuggestions = data.suggestions.filter((s: any) => 
        s.type === 'factual' || s.type === 'prompt'
      );

      setSuggestions(validSuggestions);
    } catch (error) {
      console.error('Error getting suggestions:', error);
      // TODO: Add toast notification here
    } finally {
      setIsLoadingSuggestions(false);
    }
  };

  const handleAcceptSuggestion = (suggestion: Suggestion) => {
    if (suggestion.type === 'prompt') {
      return;
    }
    
    // Store the current value before accepting the suggestion
    setPreviousValues(prev => ({
      ...prev,
      [suggestion.field]: card.card_data[suggestion.field],
    }));

    // Accept the suggestion
    handleFieldChange(suggestion.field, suggestion.suggestedValue);
    setAcceptedFields(prev => new Set([...prev, suggestion.field]));
  };

  const handleRejectSuggestion = (suggestion: Suggestion) => {
    setSuggestions(prev => prev.filter(s => s.field !== suggestion.field));
  };

  const handleUndoSuggestion = (field: string) => {
    // Restore the previous value
    if (field in previousValues) {
      handleFieldChange(field, previousValues[field]);
      
      // Remove from accepted fields and previous values
      setAcceptedFields(prev => {
        const next = new Set(prev);
        next.delete(field);
        return next;
      });
      setPreviousValues(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const generateId = () => {
    return Math.random().toString(36).substr(2, 9);
  };

  const handleNestedInputChange = (field: string, index: number, subField: string, value: unknown) => {
    logger.debug('EditMode handleNestedInputChange:', { field, index, subField, value });
    setModifiedFields(prev => new Set([...prev, `${field}.${index}.${subField}`]));
    
    const currentField = (card.card_data[field] as unknown[]) || [];
    const updatedField = [...currentField];
    
    if (!updatedField[index]) {
      updatedField[index] = { id: generateId() };
    }
    
    updatedField[index] = {
      ...(updatedField[index] as Record<string, unknown>),
      [subField]: value
    };
    
    handleFieldChange(field, updatedField);
  };

  const handleTagDelete = (field: string, tagToDelete: string) => {
    const currentTags = (card.card_data[field] as string[]) || [];
    handleFieldChange(field, currentTags.filter(tag => tag !== tagToDelete));
  };

  const handleTagAdd = (field: string, event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      const input = event.currentTarget;
      const value = input.value.trim();
      if (value) {
        const currentTags = (card.card_data[field] as string[]) || [];
        if (!currentTags.includes(value)) {
          handleFieldChange(field, [...currentTags, value]);
        }
        input.value = '';
      }
    }
  };

  const getInputClassName = (field: string, isNested?: boolean, nestedPath?: string) => `
    w-full p-2 rounded border-2
    ${isNested && nestedPath ? modifiedFields.has(nestedPath) : modifiedFields.has(field) ? 'border-blue-400' : 'border-gray-300'}
    ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-black'}
  `;

  const isArrayType = (type: string): type is 'array' => type === 'array';

  // Add renderInput function before renderField
  const renderInput = (
    field: string,
    index: number,
    subFieldName: string,
    subFieldConfig: any,
    value: any
  ) => {
    const nestedPath = `${field}.${index}.${subFieldName}`;
    const inputType = subFieldConfig.type || 'text';
    
    switch (inputType) {
      case 'textarea':
        return (
          <textarea
            value={String(value || '')}
            onChange={(e) => {
              const newValues = [...(card.card_data[field] as any[])] as any[];
              newValues[index] = {
                ...newValues[index],
                [subFieldName]: e.target.value
              };
              handleFieldChange(field, newValues);
              setModifiedFields(prev => new Set([...prev, nestedPath]));
            }}
            className={`w-full p-2 border rounded ${modifiedFields.has(nestedPath) ? 'border-blue-400' : 'border-gray-300'} dark:bg-gray-700 dark:border-gray-600`}
            rows={3}
            aria-label={subFieldConfig.label}
            placeholder={`Enter ${subFieldConfig.label?.toLowerCase() || titleCase(subFieldName)}`}
          />
        );
      case 'hidden':
        // For hidden fields in nested objects, we don't render any UI
        // but we ensure the value is preserved in the data
        return null;
      case 'date':
        return (
          <input
            type="date"
            value={String(value || '')}
            onChange={(e) => {
              const newValues = [...(card.card_data[field] as any[])] as any[];
              newValues[index] = {
                ...newValues[index],
                [subFieldName]: e.target.value
              };
              handleFieldChange(field, newValues);
              setModifiedFields(prev => new Set([...prev, nestedPath]));
            }}
            className={`w-full p-2 border rounded ${modifiedFields.has(nestedPath) ? 'border-blue-400' : 'border-gray-300'} dark:bg-gray-700 dark:border-gray-600`}
            aria-label={subFieldConfig.label}
          />
        );
      case 'select':
        return (
          <select
            value={String(value || '')}
            onChange={(e) => {
              const newValues = [...(card.card_data[field] as any[])] as any[];
              newValues[index] = {
                ...newValues[index],
                [subFieldName]: e.target.value
              };
              handleFieldChange(field, newValues);
              setModifiedFields(prev => new Set([...prev, nestedPath]));
            }}
            className={`w-full p-2 border rounded ${modifiedFields.has(nestedPath) ? 'border-blue-400' : 'border-gray-300'} dark:bg-gray-700 dark:border-gray-600`}
            aria-label={subFieldConfig.label}
          >
            <option value="">Select an option</option>
            {(subFieldConfig.options || []).map((option: string) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        );
      default:
        return (
          <input
            type="text"
            value={String(value || '')}
            onChange={(e) => {
              const newValues = [...(card.card_data[field] as any[])] as any[];
              newValues[index] = {
                ...newValues[index],
                [subFieldName]: e.target.value
              };
              handleFieldChange(field, newValues);
              setModifiedFields(prev => new Set([...prev, nestedPath]));
            }}
            className={`w-full p-2 border rounded ${modifiedFields.has(nestedPath) ? 'border-blue-400' : 'border-gray-300'} dark:bg-gray-700 dark:border-gray-600`}
            aria-label={subFieldConfig.label}
            placeholder={`Enter ${subFieldConfig.label?.toLowerCase() || titleCase(subFieldName)}`}
          />
        );
    }
  };

  // Update the renderField function where the buttons need title attributes
  const renderField = (field: string, config: ExtendedSchemaField) => {
    const fieldId = `field-${field}`;
    const value = card.card_data[field];
    const items = config.type === 'nested' ? (value as Record<string, unknown>[]) || [] : [];
    const label = config.label || field;
    const description = typeof config.description === 'string' ? config.description : undefined;
    const placeholder = typeof config.placeholder === 'string' ? config.placeholder : `Enter ${label.toLowerCase()}`;
    
    switch (config.type) {
      case 'text':
        return (
          <div key={field} className="mb-4">
            <label htmlFor={fieldId} className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
              {label}
            </label>
            <input
              id={fieldId}
              type="text"
              value={isArrayType(config.type) ? (Array.isArray(value) ? value.join(', ') : '') : String(value || '')}
              onChange={(e) => handleFieldChange(field, e.target.value)}
              placeholder={placeholder}
              className={getInputClassName(field)}
            />
            {description && (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{description}</p>
            )}
          </div>
        );

      case 'hidden':
        // For hidden fields, we don't render any UI element
        // but we keep the value in the data model
        return null;

      case 'tags':
        const tags = (value as string[]) || [];
        return (
          <div key={field} className="mb-4">
            <label htmlFor={fieldId} className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
              {label}
            </label>
            <div className="flex flex-wrap gap-2 mb-2">
              {tags.map((tag, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2 py-1 rounded bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                >
                  {tag}
                  <button
                    type="button"
                    onClick={() => handleTagDelete(field, tag)}
                    className="ml-1 text-blue-600 hover:text-blue-800 dark:text-blue-300 dark:hover:text-blue-100"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
            <input
              id={fieldId}
              type="text"
              placeholder="Type and press Enter to add tags"
              onKeyDown={(e) => handleTagAdd(field, e)}
              className={getInputClassName(field)}
            />
            {description && (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{description}</p>
            )}
          </div>
        );

      case 'textarea':
        return (
          <div key={field} className="mb-4">
            <label htmlFor={fieldId} className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
              {label}
            </label>
            <textarea
              id={fieldId}
              value={value as string || ''}
              onChange={(e) => handleFieldChange(field, e.target.value)}
              placeholder={placeholder}
              className={getInputClassName(field)}
              rows={4}
            />
            {description && (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{description}</p>
            )}
          </div>
        );

      case 'date':
        return (
          <div key={field} className="mb-4">
            <label htmlFor={fieldId} className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
              {label}
            </label>
            <input
              id={fieldId}
              type="date"
              value={value as string || ''}
              onChange={(e) => handleFieldChange(field, e.target.value)}
              className={getInputClassName(field)}
            />
            {description && (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{description}</p>
            )}
          </div>
        );

      case 'select':
        return (
          <div key={field} className="mb-4">
            <label htmlFor={fieldId} className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
              {label}
            </label>
            <select
              id={fieldId}
              value={value as string || ''}
              onChange={(e) => handleFieldChange(field, e.target.value)}
              className={getInputClassName(field)}
            >
              {config.options?.map((option: string) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            {description && (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{description}</p>
            )}
          </div>
        );

      case 'nested':
        const nestedValues = card.card_data[field] as any[] || [];
        const nestedFields = config.fields || [];

        return (
          <div key={field} className="mb-6">
            <div className="flex justify-between mb-2">
              <h4 className="text-sm font-medium mb-2">{config.label || titleCase(field)}</h4>
              <button
                onClick={() => {
                  const newNestedValues = [...nestedValues, {}];
                  handleFieldChange(field, newNestedValues);
                }}
                className="flex items-center gap-1 text-blue-600 hover:text-blue-700 text-sm"
                title={`Add ${config.label ? 
                  (config.label.endsWith('s') ? config.label.slice(0, -1) : config.label) 
                  : titleCase(field)}`}
              >
                <IoAddOutline size={16} />
                Add {config.label ? 
                  (config.label.endsWith('s') ? config.label.slice(0, -1) : config.label) 
                  : titleCase(field)}
              </button>
            </div>
            {nestedValues.map((item, index) => (
              <div key={index} className="mb-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="flex justify-between mb-2">
                  <h5 className="font-medium">
                    {config.label ? 
                      (config.label.endsWith('s') ? config.label.slice(0, -1) : config.label) 
                      : titleCase(field)} {index + 1}
                  </h5>
                  <button
                    onClick={() => handleDeleteNestedItem(field, index)}
                    className="text-red-500 hover:text-red-700"
                    title={`Delete ${config.label ? 
                      (config.label.endsWith('s') ? config.label.slice(0, -1) : config.label) 
                      : titleCase(field)}`}
                  >
                    <IoTrashOutline size={18} />
                  </button>
                </div>
                <div className="space-y-4">
                  {nestedFields.map((subField: any) => {
                    const subFieldName = typeof subField === 'string' ? subField : subField.name;
                    const subFieldConfig = typeof subField === 'string' 
                      ? { label: titleCase(subField), type: 'text' } 
                      : subField;
                    
                    // Skip rendering hidden fields
                    if (subFieldConfig.type === 'hidden' || subFieldConfig.hidden === true) {
                      return null;
                    }
                    
                    return (
                      <div key={subFieldName} className="grid grid-cols-1 gap-2">
                        <label className="text-sm text-gray-700 dark:text-gray-300">
                          {subFieldConfig.label || titleCase(subFieldName)}
                        </label>
                        {renderInput(
                          field, 
                          index, 
                          subFieldName, 
                          subFieldConfig, 
                          nestedValues[index]?.[subFieldName] || ''
                        )}
                      </div>
                    );
                  })}
                  
                  {/* Render action buttons with field context */}
                  {renderFollowUpActions && 
                    renderFollowUpActions(
                      { ...item, __fieldName: field }, 
                      index
                    )
                  }
                </div>
              </div>
            ))}
          </div>
        );

      case 'array':
        return (
          <div key={field} className="mb-4">
            <label className="block text-sm font-medium mb-2">
              {config.label || field}
            </label>
            <ArrayInput
              label={config.label || field}
              values={Array.isArray(value) ? value : []}
              onChange={(newValues) => handleFieldChange(field, newValues)}
            />
          </div>
        );

      default:
        return null;
    }
  };

  const renderFields = () => {
    if (!schema) {
      logger.debug('No schema available for rendering fields');
      return null;
    }
    
    logger.debug('Rendering fields with schema:', { schema });
    logger.debug('Current card data:', { cardData: card.card_data });
    
    return Object.keys(schema)
      .map(field => ({
        field,
        config: schema[field] as ExtendedSchemaField,
        order: schema[field].order || 0
      }))
      .sort((a, b) => a.order - b.order)
      .filter(({ config }) => config.type !== 'hidden' && config.hidden !== true)
      .map(({ field, config }) => {
        logger.debug('Rendering field:', { field, config, value: card.card_data[field] });
        return renderField(field, config);
      });
  };

  // Handle the case when schema is not available
  if (!schema) {
    return (
      <div className="p-4">
        <div className="text-center text-gray-500 dark:text-gray-400">
          <p>Schema not found for card type: {card.card_data.type}</p>
          <p className="mt-2 text-sm">Please try refreshing the page.</p>
        </div>
      </div>
    );
  }

  // Add metadata fields that might not be in the schema
  const metadataFields: Record<string, ExtendedSchemaField> = {
    custom_tags: {
      type: 'tags',
      label: 'Tags',
      order: 1000,
    },
    date_tags: {
      type: 'tags',
      label: 'Date Tags',
      order: 1001,
    },
    status: {
      type: 'select',
      label: 'Status',
      order: 1002,
      options: ['Active', 'Archived'],
    },
  };

  const allFields = { ...schema, ...metadataFields };

  const sortByOrder = (a: [string, SchemaField], b: [string, SchemaField]): number => {
    return (a[1].order || 0) - (b[1].order || 0);
  };

  // Fix the handleDeleteNestedItem function
  const handleDeleteNestedItem = (field: string, index: number) => {
    const newValues = [...(card.card_data[field] as any[])] as any[];
    newValues.splice(index, 1);
    handleFieldChange(field, newValues);
  };

  return (
    <div className="space-y-6">
      {/* Existing Fields */}
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Edit Card Content</h2>
        <button
          onClick={handleGetSuggestions}
          disabled={isLoadingSuggestions}
          className={`px-4 py-2 rounded ${
            darkMode
              ? 'bg-blue-600 hover:bg-blue-700'
              : 'bg-blue-500 hover:bg-blue-600'
          } text-white transition-colors`}
        >
          {isLoadingSuggestions ? 'Getting Suggestions...' : 'Get Suggestions'}
        </button>
      </div>

      {suggestions.length > 0 && (
        <SuggestionPanel
          suggestions={suggestions}
          onAccept={handleAcceptSuggestion}
          onReject={handleRejectSuggestion}
          onUndo={handleUndoSuggestion}
          acceptedFields={acceptedFields}
          cardType={selectedCardType || card.card_data.type}
        />
      )}

      <div className="mt-4">
        {renderFields()}
      </div>

      {/* Linked Content Section */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-8">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium flex items-center gap-2">
            <IoLinkOutline />
            Linked Content
          </h3>
        </div>

        {/* Focus Linkage - collapsible section */}
        {focus && card.cardid && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs text-gray-500 dark:text-gray-400">
                Link to {focus.company} - {focus.role}
              </h4>
              <button
                onClick={() => setIsFocusLinkageExpanded(!isFocusLinkageExpanded)}
                className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full"
                title={isFocusLinkageExpanded ? "Collapse focus linking" : "Expand focus linking"}
              >
                {isFocusLinkageExpanded ? <IoChevronUpOutline size={16} /> : <IoChevronDownOutline size={16} />}
              </button>
            </div>
            
            <motion.div
              initial={false}
              animate={{ height: isFocusLinkageExpanded ? "auto" : 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div className="space-y-4">
                <GoalSelector 
                  cardId={card.cardid} 
                  className=""
                />
                
                {activeRubric && (
                  <RubricCriteriaSelector 
                    cardId={card.cardid} 
                    className=""
                  />
                )}
              </div>
            </motion.div>
          </div>
        )}

        {/* Work Experience Link */}
        <div className="space-y-2">
          <h4 className="text-xs text-gray-500 dark:text-gray-400">Work Experience</h4>
          {linkedContent ? (
            <LinkedContentPreview
              content={linkedContent}
              onUnlink={onUnlinkWorkExperience}
            />
          ) : (
            <div className="relative">
              <select
                onChange={(e) => onLinkWorkExperience(e.target.value)}
                value=""
                className={`
                  w-full p-2 rounded border text-sm
                  ${darkMode 
                    ? 'bg-gray-700 border-gray-600 text-white' 
                    : 'bg-white border-gray-300 text-black'
                  }
                `}
                aria-label="Select work experience to link"
              >
                <option value="">Select work experience...</option>
                {workExperiences?.map((exp) => (
                  <option key={exp.id} value={exp.id}>
                    {exp.preview.position} at {exp.preview.company}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>
    </div>
  );
} 